import React, { useEffect, useRef, useState } from "react";
import { useInfiniteQuery } from "react-query";
import { useSearchParams } from "react-router-dom";
import {
  Flex,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  useDisclosure,
} from "@chakra-ui/react";
import Joi from "joi";
import moment from "moment";

import { TableProps } from "@alpacahq/alpaca-component-library";

import {
  AcatsDtccTransferStatus,
  AcatsDtccTransferType,
  AcatsSortDirection,
  AcatsTransfer,
  AcatsTransferDirection,
  AcatsTransferStatus,
} from "../../api/acats/acats";
import {
  listAcatsTransfers,
  ListAcatsTransfersParams,
} from "../../api/acats/listAcatsTransfers";
import {
  dateRangeToPill,
  FilterButton,
  FilterDateSelect,
  FilterDrawer,
  FilterSelect,
} from "../../components/filter";
import Tab from "../../components/general/Tab";
import Header from "../../components/layout/Header";

import AcatsTransfersTable, { DEFAULT_PAGE_SIZE } from "./AcatsTransfersTable";

const TRANSFER_TYPES: AcatsDtccTransferType[] = [
  "FUL",
  "FRV",
  "MFC",
  "PTD",
  "PTR",
  "RCL",
  "RCR",
  "PTF",
];

const TRANSFER_DIRECTIONS: AcatsTransferDirection[] = ["INCOMING", "OUTGOING"];

const TRANSFER_STATUSES: AcatsTransferStatus[] = [
  "PENDING",
  "IN_PROGRESS",
  "REJECTED",
  "SETTLED",
];
const DTCC_STATUSES: AcatsDtccTransferStatus[] = [
  "REQUEST",
  "REQUEST_ADJUST",
  "REQUEST_ADJUST_PAST",
  "REQUEST_PAST",
  "REQUEST_REJECT",
  "REVIEW",
  "REVIEW_ADJUST_DELIVERER",
  "REVIEW_ERROR",
  "REVIEW_ACCELERATE",
  "REVIEW_ADJUST_RECEIVER_ACCELERATE",
  "SETTLE_PREP",
  "SETTLE_CLOSE",
  "CLOSE_PURGE",
  "REQUEST_PTR",
  "MEMO_PURGE_PARTIAL_TRANSFER_REQUEST_RECEIVER",
  "REJECT",
];

const CREATED_TIMEFRAMES = [
  "anytime",
  "24-hours",
  "7-days",
  "14-days",
  "30-days",
  "60-days",
  "range",
];

interface FilterForm {
  transfer_type: AcatsDtccTransferType | "All";
  transfer_direction: AcatsTransferDirection;
  transfer_status: AcatsTransferStatus | "All";
  dtcc_status: AcatsDtccTransferStatus | "All";
  created_timeframe: string;
  created_since: moment.Moment | null;
  created_until: moment.Moment | null;
}

const defaultFilters: FilterForm = {
  transfer_type: "All",
  transfer_direction: "INCOMING",
  transfer_status: "All",
  dtcc_status: "All",
  created_timeframe: "anytime",
  created_since: null,
  created_until: null,
};

const PREFETCH_INITIAL_PAGE_COUNT = 10;

const schema = Joi.object<FilterForm>({
  transfer_type: Joi.string()
    .valid(...TRANSFER_TYPES)
    .failover(defaultFilters.transfer_type)
    .default(defaultFilters.transfer_type),
  transfer_direction: Joi.string()
    .valid(...TRANSFER_DIRECTIONS)
    .failover(defaultFilters.transfer_direction)
    .default(defaultFilters.transfer_direction),
  transfer_status: Joi.string()
    .valid(...TRANSFER_STATUSES)
    .failover(defaultFilters.transfer_status)
    .default(defaultFilters.transfer_status),
  dtcc_status: Joi.string()
    .valid(...DTCC_STATUSES)
    .failover(defaultFilters.dtcc_status)
    .default(defaultFilters.dtcc_status),
  created_timeframe: Joi.string()
    .valid(...CREATED_TIMEFRAMES)
    .failover(defaultFilters.created_timeframe)
    .default(defaultFilters.created_timeframe),
  created_since: Joi.date()
    .failover(defaultFilters.created_since)
    .default(defaultFilters.created_since)
    .custom((value) => moment(value)),
  created_until: Joi.date()
    .failover(defaultFilters.created_until)
    .default(defaultFilters.created_until)
    .custom((value) => moment(value)),
});

const createInitialFilters = (searchParams: URLSearchParams): FilterForm => {
  const result = schema.validate(Object.fromEntries(searchParams.entries()), {
    stripUnknown: true,
  });

  return result.value ?? defaultFilters;
};

const AcatsTransfers = (): JSX.Element => {
  const { isOpen, onClose, onOpen } = useDisclosure();

  const skipPageResetRef = useRef(false);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const [sortBy, setSortBy] = useState<AcatsSortDirection>("DESC");
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState(createInitialFilters(searchParams));
  const [appliedFilters, setAppliedFilters] = useState(
    createInitialFilters(searchParams)
  );

  const { data, fetchNextPage, hasNextPage, isLoading } = useInfiniteQuery(
    ["acats", pageSize, appliedFilters, sortBy],
    ({ pageParam }) => {
      const params: ListAcatsTransfersParams = {
        limit: pageSize,
        sort_direction: sortBy,
        page_token: pageParam as string,
        transfer_direction: appliedFilters.transfer_direction,
      };

      if (appliedFilters.transfer_type !== "All") {
        params.transfer_type = appliedFilters.transfer_type;
      }

      if (appliedFilters.transfer_status !== "All") {
        params.transfer_status = appliedFilters.transfer_status;
      }

      if (appliedFilters.dtcc_status !== "All") {
        params.dtcc_status = appliedFilters.dtcc_status;
      }

      if (appliedFilters.created_since && appliedFilters.created_until) {
        params.since = appliedFilters.created_since
          .startOf("day")
          .format(moment.defaultFormatUtc);
        params.until = appliedFilters.created_until
          .endOf("day")
          .format(moment.defaultFormatUtc);
      }

      return listAcatsTransfers(params);
    },
    {
      getNextPageParam: (lastPage) => {
        return lastPage.page_token_next;
      },
    }
  );

  const transfers = data?.pages?.flatMap(({ transfers }) => transfers) ?? [];
  const tabIndex = appliedFilters.transfer_direction === "INCOMING" ? 0 : 1;
  const totalPages = data?.pages.length ?? 0;

  const onTabChange = (index: number) => {
    setAppliedFilters((filters) => {
      const updatedFilters: FilterForm = {
        ...filters,
        transfer_direction: index === 0 ? "INCOMING" : "OUTGOING",
      };

      updateSearchParams(updatedFilters);
      return updatedFilters;
    });
  };

  const updateSearchParams = (filters: FilterForm) => {
    const searchParams = new URLSearchParams({
      transfer_direction: filters.transfer_direction,
    });

    if (filters.transfer_type !== "All") {
      searchParams.set("transfer_type", filters.transfer_type);
    }

    if (filters.transfer_status !== "All") {
      searchParams.set("transfer_status", filters.transfer_status);
    }

    if (filters.dtcc_status !== "All") {
      searchParams.set("dtcc_status", filters.dtcc_status);
    }

    if (
      filters.created_timeframe !== "anytime" &&
      filters.created_since &&
      filters.created_until
    ) {
      searchParams.set("created_timeframe", filters.created_timeframe);
      searchParams.set(
        "created_since",
        filters.created_since.format("YYYY-MM-DD")
      );
      searchParams.set(
        "created_until",
        filters.created_until.format("YYYY-MM-DD")
      );
    }

    setSearchParams(searchParams);
  };

  const applyFilter = () => {
    setAppliedFilters(filters);
    updateSearchParams(filters);
    onClose();
    skipPageResetRef.current = false;
  };

  const removeFilter = (filterKey: string) => {
    setAppliedFilters((filters) => {
      const updatedFilters = { ...filters };

      if (filterKey === "transfer_type") {
        updatedFilters.transfer_type = defaultFilters.transfer_type;
      }

      if (filterKey === "transfer_status") {
        updatedFilters.transfer_status = defaultFilters.transfer_status;
      }

      if (filterKey === "dtcc_status") {
        updatedFilters.dtcc_status = defaultFilters.dtcc_status;
      }

      if (filterKey === "created_at") {
        updatedFilters.created_timeframe = "anytime";
        updatedFilters.created_since = null;
        updatedFilters.created_until = null;
      }

      updateSearchParams(updatedFilters);
      return updatedFilters;
    });

    skipPageResetRef.current = false;
  };

  const openFilter = () => {
    setFilters(appliedFilters);
    onOpen();
  };

  const closeFilter = () => {
    onClose();
  };

  const setFilterValue = (
    key: keyof FilterForm,
    value: FilterForm[typeof key]
  ) => {
    setFilters((filters) => ({
      ...filters,
      [key]: value,
    }));
  };

  const filterPills = {
    transfer_type: appliedFilters.transfer_type,
    transfer_status: appliedFilters.transfer_status,
    dtcc_status: appliedFilters.dtcc_status,
    created_at: dateRangeToPill([
      appliedFilters.created_since,
      appliedFilters.created_until,
    ]),
  };

  const onFetchData: TableProps<AcatsTransfer>["onFetchData"] = ({
    pageIndex,
    pageSize: updatedPageSize,
    sortBy,
  }) => {
    const currentPage = pageIndex + 1;
    const threshold = totalPages - 1;
    if (hasNextPage && currentPage === threshold) {
      fetchNextPage();
    }

    if (sortBy?.[0]?.id === "created_at") {
      setSortBy(sortBy[0].desc === true ? "DESC" : "ASC");
    }

    if (updatedPageSize !== pageSize) {
      setPageSize(pageSize);
    }

    skipPageResetRef.current = true;
  };

  useEffect(() => {
    if (hasNextPage && totalPages < PREFETCH_INITIAL_PAGE_COUNT) {
      fetchNextPage();
    }
  }, [hasNextPage, totalPages]);

  return (
    <>
      <Header title="ACATS > Transfers" />
      <Tabs index={tabIndex} onChange={onTabChange} isLazy>
        <Flex justifyContent="space-between" alignItems="center">
          <TabList>
            <Tab
              text="Incoming"
              isSelected={appliedFilters.transfer_direction === "INCOMING"}
            />
            <Tab
              text="Outgoing"
              isSelected={appliedFilters.transfer_direction === "OUTGOING"}
            />
          </TabList>
          <FilterButton
            filterPills={filterPills}
            openFilter={openFilter}
            removeFilter={removeFilter}
          />
        </Flex>
        <TabPanels>
          <TabPanel>
            <AcatsTransfersTable
              data={transfers}
              isLoading={isLoading}
              onFetchData={onFetchData}
              skipPageResetRef={skipPageResetRef}
            />
          </TabPanel>
          <TabPanel>
            <AcatsTransfersTable
              data={transfers}
              isLoading={isLoading}
              onFetchData={onFetchData}
              skipPageResetRef={skipPageResetRef}
            />
          </TabPanel>
        </TabPanels>
      </Tabs>
      <FilterDrawer isOpen={isOpen} onClose={closeFilter} onApply={applyFilter}>
        <FilterSelect
          header="Type"
          options={["All", ...TRANSFER_TYPES]}
          onSelect={(value) => setFilterValue("transfer_type", value)}
          selected={filters.transfer_type}
          noOptionFmt
        />
        <FilterSelect
          header="Status"
          options={["All", ...TRANSFER_STATUSES]}
          onSelect={(value) => setFilterValue("transfer_status", value)}
          selected={filters.transfer_status}
          noOptionFmt
        />
        <FilterSelect
          header="DTCC Status"
          options={["All", ...DTCC_STATUSES]}
          onSelect={(value) => setFilterValue("dtcc_status", value)}
          selected={filters.dtcc_status}
          noOptionFmt
        />
        <FilterDateSelect
          header="Created At"
          onSelect={(value) => setFilterValue("created_timeframe", value)}
          defaultSelect={filters.created_timeframe}
          onDateSelect={(start, end) => {
            setFilterValue("created_since", start);
            setFilterValue("created_until", end);
          }}
          value={[filters.created_since, filters.created_until]}
        />
      </FilterDrawer>
    </>
  );
};

export default AcatsTransfers;
